import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlContainer, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { isStringArray } from '@platform/shared/utilities/obserables-utils';
import { OptionItem, provideParentFormGroup } from '../forms.utils';
import { PreComponent } from '../../pre/pre.component';

@Component({
  selector: 'app-form-autocomplete',
  standalone: true,
  templateUrl: './form-autocomplete.component.html',
  styleUrls: ['./form-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [provideParentFormGroup()],
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, MatInputModule, MatAutocompleteModule, PreComponent],
})
export class FormAutocompleteComponent {
  @Input() labelT = '';
  @Input({ required: true }) controlKey: string;
  @Input() items: OptionItem[] | string[] = [];
  @Input() validationRules: any = {};

  parentContainer: ControlContainer = inject(ControlContainer);
  translate = inject(TranslateService);

  errorMap: any = {
    required: 'shared.error.required',
    maxlength: 'shared.error.max-length',
  };

  get itemsInternal(): OptionItem[] {
    if (!this.items || !this.items.length) {
      return [];
    }

    if (isStringArray(this.items)) {
      return this.items.map((item) => ({ label: item, value: item }));
    } else {
      return this.items;
    }
  }

  get parentFormGroup() {
    return this.parentContainer.control as FormGroup;
  }

  get control() {
    return this.parentFormGroup.get(this.controlKey);
  }

  get errorsArray() {
    return Object.entries({ ...this.errorMap, ...this.validationRules })
      .map(([key, messageT]) => ({
        key,
        messageT: typeof messageT === 'string' ? messageT : null,
        messageFn: typeof messageT === 'function' ? messageT : null,
      }))
      .filter(({ key }) => this.control?.hasError(key));
  }

  trackBy(err: any) {
    return err.key;
  }

  displayErrorMessage(err: any) {
    if (err.messageT) {
      return this.translate.instant(err.messageT);
    }

    if (err.messageFn) {
      return err.messageFn(this.translate);
    }

    return `${err.key} is not valid`;
  }
}
